import { Service, inject } from '@angular/core';
import { Router } from '@angular/router';

import { ApiService } from './api.service';
import { RegistreService } from './registre.service';
import { SnackBarService } from './snackbar.service';

import { User, UserLogin, ApiAuthResponse, JWTToken } from '../models/user.model';
import { AlertsService } from './alerts.service';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AuthStore } from '../state/auth.store';
import { UiStore } from '../state/ui.store';
import { executa } from './executa';

// refresh token-ul e păstrat în sessionStorage: sesiunea rezistă la F5 în același tab,
// dar se pierde la închiderea tabului; access token-ul rămâne doar în memorie
const CHEIE_SESIUNE = 'cmr-avizari.refresh-token';

function citesteSesiunea(): string | null {
  try {
    return sessionStorage.getItem(CHEIE_SESIUNE);
  } catch {
    return null;
  }
}

function scrieSesiunea(refreshToken: string | null) {
  try {
    if (refreshToken) {
      sessionStorage.setItem(CHEIE_SESIUNE, refreshToken);
    } else {
      sessionStorage.removeItem(CHEIE_SESIUNE);
    }
  } catch {
    // stocare indisponibilă (ex. blocată de browser): sesiunea rămâne doar în memorie
  }
}

@Service()
export class AuthService {
  private readonly authStore = inject(AuthStore);
  private readonly uiStore = inject(UiStore);
  private router = inject(Router);
  private apiService = inject(ApiService);
  private regService = inject(RegistreService);
  private snackBar = inject(SnackBarService);
  private alertService = inject(AlertsService);

  login(user: UserLogin) {
    return executa(this.apiService.callLogin(user), (response: ApiAuthResponse) => {
        this.snackBar.showSnackbar('Autentificare cu succes');
        this.pornesteSesiunea(response);
        this.router.navigateByUrl(this.adresaDupaLogin());
    }, this.snackBar);
  }

  /**
   * La pornirea aplicației (F5): dacă tabul are un refresh token salvat, cere tokenuri noi
   * și reface sesiunea înainte de prima navigare. Un token expirat sau respins e șters,
   * iar utilizatorul ajunge la login ca înainte.
   */
  reiaSesiunea(): Observable<void> {
    const refreshToken = citesteSesiunea();
    if (!refreshToken) {
      return of(undefined);
    }
    return this.apiService.callRefreshToken(refreshToken).pipe(
      tap(response => this.pornesteSesiunea(response)),
      map(() => undefined),
      catchError(() => {
        scrieSesiunea(null);
        return of(undefined);
      }),
    );
  }

  private pornesteSesiunea(response: ApiAuthResponse) {
    const userData = this.getUser(response);
    this.uiStore.setUser(userData);
    if (userData.role === 0) {
      this.authStore.setAdmin();
    }
    this.authStore.setAuthenticated();
    this.authStore.setAccessToken(response.access_token);
    this.retineRefreshToken(response.refresh_token ?? '');
    this.regService.getRegistre();
    this.alertService.getAlert('transfer');
  }

  private retineRefreshToken(refreshToken: string) {
    this.authStore.setRefreshToken(refreshToken);
    scrieSesiunea(refreshToken);
  }

  // pagina ceruta inainte de login (?returnUrl=), doar adrese din aplicatie
  private adresaDupaLogin(): string {
    const adresa = this.router.routerState.snapshot.root.queryParamMap.get('returnUrl');
    return adresa && adresa.startsWith('/') && !adresa.startsWith('//') ? adresa : '/';
  }

  refreshToken(tokenRefresh: string) {
    return this.apiService.callRefreshToken(tokenRefresh).pipe(
      tap((response: ApiAuthResponse) => {
        this.authStore.setAccessToken(response.access_token);
        // backend-ul trimite și un refresh token nou: sesiunea se prelungește cât timp e folosită
        if (response.refresh_token) {
          this.retineRefreshToken(response.refresh_token);
        }
      }),
    );
  }

  getUser(response: ApiAuthResponse) {
    const tokenExpanded: JWTToken = this.jwt_decode(response.access_token);
    const user: User = tokenExpanded.claims;
    return user;
  }

  /** Sesiunea a expirat: după o nouă autentificare se revine la pagina curentă. */
  logout() {
    this.inchideSesiunea();
    this.snackBar.showSnackbar('Autentificarea a expirat');
    const adresa = this.router.url;
    const returnUrl = adresa !== '/' && !adresa.startsWith('/autorizare') ? adresa : undefined;
    this.router.navigate(['/autorizare'], { queryParams: { returnUrl } });
  }

  /** Ieșire cerută de utilizator (meniul din antet). */
  iesire() {
    this.inchideSesiunea();
    this.router.navigateByUrl('/autorizare');
  }

  private inchideSesiunea() {
    this.authStore.clear();
    scrieSesiunea(null);
    this.uiStore.hideSidenav();
    this.uiStore.removeUser();
  }

  // payload-ul JWT e base64url cu text UTF-8 (numele pot avea diacritice)
  private jwt_decode(token: string): JWTToken {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  }
}

